import { useState, useEffect, useCallback, useRef } from 'react'
import { Room, RoomEvent } from 'livekit-client'
import type { TranscriptionSegment, Participant } from 'livekit-client'

export interface TranscriptionMessage {
  id: string
  text: string
  isFinal: boolean
  timestamp: number
}

interface UseAgentTranscriptionOptions {
  room: Room | null
}

interface UseAgentTranscriptionReturn {
  /** All completed transcription messages */
  messages: TranscriptionMessage[]
  /** Current interim text (agent is still speaking) */
  interimText: string
  /** Clear all messages (e.g., on session end) */
  clear: () => void
}

export function useAgentTranscription({
  room,
}: UseAgentTranscriptionOptions): UseAgentTranscriptionReturn {
  const [messages, setMessages] = useState<TranscriptionMessage[]>([])
  const [interimText, setInterimText] = useState('')

  // Track segments by ID to handle updates
  const segmentMapRef = useRef<Map<string, TranscriptionMessage>>(new Map())

  const handleTranscriptionReceived = useCallback(
    (
      segments: TranscriptionSegment[],
      participant?: Participant
    ) => {
      // Only process transcriptions from remote participants (agent)
      // Local participant is the user - we don't want their transcription
      if (!participant || participant === room?.localParticipant) {
        return
      }

      for (const segment of segments) {
        const id = segment.id
        const text = segment.text.trim()
        const isFinal = segment.final

        if (!text) continue

        if (isFinal) {
          // Final segment - add to messages and clear from interim
          setMessages(prev => {
            // Check if we already have this segment
            const existingIndex = prev.findIndex(m => m.id === id)
            if (existingIndex >= 0) {
              // Update existing
              const updated = [...prev]
              updated[existingIndex] = {
                id,
                text,
                isFinal: true,
                timestamp: Date.now(),
              }
              return updated
            }
            // Add new
            return [...prev, {
              id,
              text,
              isFinal: true,
              timestamp: Date.now(),
            }]
          })

          // Clear interim if it matches this segment
          segmentMapRef.current.delete(id)

          // Update interim text from remaining non-final segments
          const remaining = Array.from(segmentMapRef.current.values())
            .filter(s => !s.isFinal)
            .map(s => s.text)
            .join(' ')
          setInterimText(remaining)
        } else {
          // Interim segment - update the map and rebuild interim text
          segmentMapRef.current.set(id, {
            id,
            text,
            isFinal: false,
            timestamp: Date.now(),
          })

          // Rebuild interim text from all non-final segments
          const interimParts = Array.from(segmentMapRef.current.values())
            .filter(s => !s.isFinal)
            .map(s => s.text)
          setInterimText(interimParts.join(' '))
        }
      }
    },
    [room]
  )

  const clear = useCallback(() => {
    setMessages([])
    setInterimText('')
    segmentMapRef.current.clear()
  }, [])

  useEffect(() => {
    if (!room) return

    room.on(RoomEvent.TranscriptionReceived, handleTranscriptionReceived)

    return () => {
      room.off(RoomEvent.TranscriptionReceived, handleTranscriptionReceived)
    }
  }, [room, handleTranscriptionReceived])

  // Clear when room changes (new session)
  useEffect(() => {
    clear()
  }, [room, clear])

  return {
    messages,
    interimText,
    clear,
  }
}
